import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  IconButton,
  Input,
  NativeSelectField,
  NativeSelectRoot,
  Spinner,
  Stack,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { useCallback, useMemo, useState } from "react"
import { FaChevronLeft, FaChevronRight } from "react-icons/fa"
import {
  FiArrowRight,
  FiGitMerge,
  FiRefreshCw,
  FiSearch,
  FiTag,
  FiTrash,
} from "react-icons/fi"
import { z } from "zod"

import {
  ContentService,
  OrganizationsService,
  type TopicWithCountPublic,
  type TopicsWithCountPublic,
  type UserPublic,
} from "@/client"
import type { ApiError } from "@/client/core/ApiError"
import type { MembershipPublic } from "@/client/types.gen"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { emptyStateCopy } from "@/components/Common/emptyStateCopy"
import { PageShell, PageSkeleton } from "@/components/Layout"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { Field } from "@/components/ui/field"
import { InputGroup } from "@/components/ui/input-group"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination"
import { Radio, RadioGroup } from "@/components/ui/radio"
import { toaster } from "@/components/ui/toaster"
import { Tooltip } from "@/components/ui/tooltip"
import { useDeleteTopic } from "@/hooks/useDeleteTopic"
import { useTopicMerge } from "@/hooks/useTopicMerge"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import { handleError } from "@/utils"

const searchSchema = z.object({
  q: z.string().optional(),
  sort: z.enum(["name", "count"]).catch("name"),
  sortDir: z.enum(["asc", "desc"]).catch("asc"),
  page: z.number().catch(1),
})

const PAGE_SIZE = 20

function getTopicsQueryOptions({
  page,
  q,
  sort,
  sortDir,
}: {
  page: number
  q?: string
  sort: "name" | "count"
  sortDir: "asc" | "desc"
}) {
  return {
    queryKey: ["admin-topics", { page, q, sort, sortDir }],
    queryFn: () =>
      ContentService.listTopics({
        page,
        pageSize: PAGE_SIZE,
        q: q?.trim() || undefined,
        sort,
        sortDir,
      }),
    staleTime: 30 * 1000,
    placeholderData: (previous: TopicsWithCountPublic | undefined) => previous,
  }
}

export const Route = createFileRoute("/_layout/admin-topics/")({
  component: AdminTopicsPage,
  validateSearch: (search) => searchSchema.parse(search),
})

type SortOption = {
  label: string
  value: "name" | "count"
}

const sortOptions: SortOption[] = [
  { label: "Name", value: "name" },
  { label: "Question count", value: "count" },
]

function AdminTopicsPage() {
  const navigate = useNavigate({ from: Route.fullPath })
  const { q, page, sort, sortDir } = Route.useSearch()
  const [searchInput, setSearchInput] = useState(q ?? "")
  const [selectedTopics, setSelectedTopics] = useState<
    Record<string, TopicWithCountPublic>
  >({})
  const [isMergeDialogOpen, setMergeDialogOpen] = useState(false)
  const [destinationTopicId, setDestinationTopicId] = useState<string | null>(
    null,
  )
  const [topicToDelete, setTopicToDelete] =
    useState<TopicWithCountPublic | null>(null)
  const queryClient = useQueryClient()
  const breadcrumbs = useRouteBreadcrumbs("admin-topics.index")
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])

  const { data: memberships } = useQuery<MembershipPublic[]>({
    queryKey: ["myMemberships", currentUser?.id],
    queryFn: () => OrganizationsService.getMyMemberships(),
    enabled: Boolean(currentUser && !currentUser.is_superuser),
    staleTime: 5 * 60 * 1000,
  })

  const isOrgAdmin =
    currentUser?.is_superuser ||
    memberships?.some((membership) => membership.role === "admin")

  const { data, isLoading, isFetching, isPlaceholderData } = useQuery<
    TopicsWithCountPublic,
    ApiError
  >({
    ...getTopicsQueryOptions({ page, q, sort, sortDir }),
  })

  const topics = data?.data ?? []
  const total = data?.count ?? 0
  const selectedEntries = useMemo(
    () => Object.values(selectedTopics),
    [selectedTopics],
  )

  const mergeMutation = useTopicMerge()
  const deleteTopicMutation = useDeleteTopic()

  const setSearch = (next: Partial<z.infer<typeof searchSchema>>) => {
    navigate({
      search: (prev: z.infer<typeof searchSchema>) => ({
        ...prev,
        ...next,
      }),
    })
  }

  const setSelectionForTopic = (
    topic: TopicWithCountPublic,
    checked: boolean,
  ) => {
    setSelectedTopics((prev) => {
      const next = { ...prev }
      if (checked) {
        next[topic.id] = topic
      } else {
        delete next[topic.id]
      }
      return next
    })
  }

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    setSearch({ q: searchInput || undefined, page: 1 })
  }

  const handleClearSelection = () => {
    setSelectedTopics({})
  }

  const openMergeDialog = () => {
    const first = selectedEntries[0]
    setDestinationTopicId(first?.id ?? null)
    setMergeDialogOpen(true)
  }

  const handleConfirmMerge = useCallback(async () => {
    if (!destinationTopicId) {
      return
    }
    const sourceIds = selectedEntries
      .filter((topic) => topic.id !== destinationTopicId)
      .map((topic) => topic.id)

    if (sourceIds.length === 0) {
      return
    }

    try {
      await mergeMutation.mutateAsync({
        destination_topic_id: destinationTopicId,
        source_topic_ids: sourceIds,
      })
      toaster.success({
        title: "Topics merged",
        description: "Topics were merged successfully.",
      })
      setSelectedTopics({})
      setMergeDialogOpen(false)
      queryClient.invalidateQueries({ queryKey: ["admin-topics"] })
    } catch (err) {
      handleError(err as ApiError)
    }
  }, [destinationTopicId, mergeMutation, queryClient, selectedEntries])

  const showMergeBar = selectedEntries.length >= 2

  const handleConfirmDelete = async () => {
    if (!topicToDelete) {
      return
    }
    try {
      await deleteTopicMutation.mutateAsync({ topicId: topicToDelete.id })
      toaster.success({
        title: "Topic deleted",
        description: `"${topicToDelete.name}" was removed.`,
      })
      setTopicToDelete(null)
    } catch (err) {
      handleError(err as ApiError)
    }
  }

  const refreshAction = (
    <Button
      variant="outline"
      gap={2}
      loading={isFetching}
      onClick={() =>
        queryClient.invalidateQueries({ queryKey: ["admin-topics"] })
      }
    >
      <FiRefreshCw />
      Refresh
    </Button>
  )

  if (!isOrgAdmin) {
    return (
      <PageShell
        title="Topic Management"
        description="Only organization admins can manage topics."
        breadcrumbs={breadcrumbs}
        actions={refreshAction}
      >
        <AppEmptyState
          title={emptyStateCopy.permissions.insufficient.title}
          description={emptyStateCopy.permissions.insufficient.description}
          tone="warning"
          data-testid="admin-topics-permissions-empty-state"
        />
      </PageShell>
    )
  }

  if (isLoading && !data) {
    return (
      <PageShell
        title="Topic Management"
        description="Search, merge, and clean up topics across your organization."
        breadcrumbs={breadcrumbs}
        actions={refreshAction}
      >
        <PageSkeleton variant="table" rows={6} />
      </PageShell>
    )
  }

  return (
    <PageShell
      title="Topic Management"
      description="Search, merge, and clean up topics across your organization."
      breadcrumbs={breadcrumbs}
      actions={refreshAction}
    >
      <Stack gap={6}>
        <Grid
          templateColumns={{ base: "1fr", lg: "repeat(2, minmax(0, 1fr))" }}
          gap={4}
        >
          <Card.Root>
            <Card.Body>
              <Stack gap={3}>
                <Flex align="center" gap={2}>
                  <FiTag />
                  <Text fontWeight="semibold">Topic Proposals</Text>
                </Flex>
                <Text color="fg.muted" fontSize="sm">
                  Review proposed topic names before approving them into the
                  shared topic library.
                </Text>
                <Link to="/admin-topic-proposals">
                  <Button variant="outline" alignSelf="flex-start" gap={2}>
                    Review proposals
                    <FiArrowRight />
                  </Button>
                </Link>
              </Stack>
            </Card.Body>
          </Card.Root>

          <Card.Root>
            <Card.Body>
              <Stack gap={3}>
                <Flex align="center" gap={2}>
                  <FiGitMerge />
                  <Text fontWeight="semibold">Topic Merges</Text>
                </Flex>
                <Text color="fg.muted" fontSize="sm">
                  Inspect suggested merges for near-duplicate topics before
                  applying destructive changes.
                </Text>
                <Link to="/admin-topic-merge">
                  <Button variant="outline" alignSelf="flex-start" gap={2}>
                    Review merges
                    <FiArrowRight />
                  </Button>
                </Link>
              </Stack>
            </Card.Body>
          </Card.Root>
        </Grid>

        <Box
          as="form"
          onSubmit={handleSearchSubmit}
          display="flex"
          gap={4}
          flexDir={{ base: "column", md: "row" }}
          mb={4}
        >
          <Field label="Search topics" width="100%">
            <InputGroup flex="1" startElement={<FiSearch />}>
              <Input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search by name"
              />
            </InputGroup>
          </Field>
          <Field label="Sort by" width={{ base: "100%", md: "240px" }}>
            <NativeSelectRoot>
              <NativeSelectField
                value={sort}
                onChange={(event) =>
                  setSearch({ sort: event.target.value as "name" | "count" })
                }
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </NativeSelectField>
            </NativeSelectRoot>
          </Field>
          <Field label="Direction" width={{ base: "100%", md: "180px" }}>
            <NativeSelectRoot>
              <NativeSelectField
                value={sortDir}
                onChange={(event) =>
                  setSearch({ sortDir: event.target.value as "asc" | "desc" })
                }
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </NativeSelectField>
            </NativeSelectRoot>
          </Field>
          <Button type="submit" alignSelf={{ base: "stretch", md: "flex-end" }}>
            Search
          </Button>
        </Box>

        {showMergeBar && (
          <Flex
            p={4}
            borderWidth="1px"
            borderRadius="lg"
            bg="bg.muted"
            align="center"
            justify="space-between"
            flexWrap="wrap"
            gap={3}
            mb={4}
          >
            <Text>
              {selectedEntries.length} topic
              {selectedEntries.length > 1 ? "s" : ""} selected for merge
            </Text>
            <Flex gap={3}>
              <Button variant="outline" gap={2} onClick={handleClearSelection}>
                <FiTrash />
                Clear selection
              </Button>
              <Button colorPalette="red" onClick={openMergeDialog}>
                Merge topics
              </Button>
            </Flex>
          </Flex>
        )}

        {/* Desktop */}
        <Box display={{ base: "none", md: "block" }}>
          <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
            <Table.Root size={{ base: "sm", md: "md" }}>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader w="40px" />
                  <Table.ColumnHeader>Name</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="right">
                    Questions
                  </Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="right">
                    Actions
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {isLoading ? (
                  <Table.Row>
                    <Table.Cell colSpan={4}>
                      <Flex justify="center" py={6}>
                        <Spinner />
                      </Flex>
                    </Table.Cell>
                  </Table.Row>
                ) : topics.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={4}>
                      <AppEmptyState
                        title={emptyStateCopy.topics.none.title}
                        description={emptyStateCopy.topics.none.description}
                        variant="compact"
                        icon={<FiSearch />}
                        data-testid="admin-topics-empty-state"
                      />
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  topics.map((topic) => {
                    const isSelected = Boolean(selectedTopics[topic.id])
                    return (
                      <Table.Row
                        key={topic.id}
                        opacity={isPlaceholderData ? 0.6 : 1}
                      >
                        <Table.Cell>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(event) =>
                              setSelectionForTopic(
                                topic,
                                Boolean(event.checked),
                              )
                            }
                            aria-label={`Select ${topic.name}`}
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Flex align="center" gap={3}>
                            <Link
                              to="/admin-topics/$topicId"
                              params={{ topicId: topic.id }}
                            >
                              <Text fontWeight="medium">{topic.name}</Text>
                            </Link>
                            {isSelected && (
                              <Badge colorPalette="blue">Selected</Badge>
                            )}
                          </Flex>
                        </Table.Cell>
                        <Table.Cell textAlign="right">
                          {topic.question_count}
                        </Table.Cell>
                        <Table.Cell textAlign="right">
                          <Flex justify="flex-end" gap={2} wrap="wrap">
                            <Link
                              to="/admin-topics/$topicId"
                              params={{ topicId: topic.id }}
                            >
                              <Button size="sm" variant="ghost">
                                View questions
                              </Button>
                            </Link>
                            <Tooltip
                              content="Cannot delete a topic with questions"
                              positioning={{ placement: "top" }}
                              disabled={topic.question_count === 0}
                            >
                              <Button
                                size="sm"
                                colorPalette="red"
                                variant="outline"
                                disabled={topic.question_count > 0}
                                onClick={() => setTopicToDelete(topic)}
                              >
                                Delete
                              </Button>
                            </Tooltip>
                          </Flex>
                        </Table.Cell>
                      </Table.Row>
                    )
                  })
                )}
              </Table.Body>
            </Table.Root>
          </Box>
        </Box>

        {/* Mobile cards */}
        <VStack display={{ base: "flex", md: "none" }} gap={3} align="stretch">
          {isLoading ? (
            <Flex justify="center" py={6}>
              <Spinner />
            </Flex>
          ) : topics.length === 0 ? (
            <AppEmptyState
              title={emptyStateCopy.topics.none.title}
              description={emptyStateCopy.topics.none.description}
              variant="compact"
              icon={<FiSearch />}
            />
          ) : (
            topics.map((topic) => {
              const isSelected = Boolean(selectedTopics[topic.id])
              return (
                <Card.Root
                  key={topic.id}
                  opacity={isPlaceholderData ? 0.6 : 1}
                  borderLeftWidth={isSelected ? "4px" : undefined}
                  borderLeftColor={isSelected ? "blue.fg" : undefined}
                >
                  <Card.Body>
                    <Stack gap={1} flex="1" minW={0}>
                      <Flex align="center" gap={2}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(event) =>
                            setSelectionForTopic(topic, Boolean(event.checked))
                          }
                          aria-label={`Select ${topic.name}`}
                        />
                        <Link
                          to="/admin-topics/$topicId"
                          params={{ topicId: topic.id }}
                        >
                          <Text fontWeight="medium">{topic.name}</Text>
                        </Link>
                        {isSelected && (
                          <Badge colorPalette="blue" size="sm">
                            Selected
                          </Badge>
                        )}
                      </Flex>
                      <Text fontSize="sm" color="fg.muted">
                        {topic.question_count} question
                        {topic.question_count !== 1 ? "s" : ""}
                      </Text>
                    </Stack>
                    <Flex gap={2} mt={3} wrap="wrap">
                      <Link
                        to="/admin-topics/$topicId"
                        params={{ topicId: topic.id }}
                      >
                        <Button size="sm" variant="ghost">
                          View questions
                        </Button>
                      </Link>
                      <Tooltip
                        content="Cannot delete a topic with questions"
                        positioning={{ placement: "top" }}
                        disabled={topic.question_count === 0}
                      >
                        <Button
                          size="sm"
                          colorPalette="red"
                          variant="outline"
                          disabled={topic.question_count > 0}
                          onClick={() => setTopicToDelete(topic)}
                        >
                          Delete
                        </Button>
                      </Tooltip>
                    </Flex>
                  </Card.Body>
                </Card.Root>
              )
            })
          )}
        </VStack>

        <Flex justify="space-between" align="center" mt={4} flexWrap="wrap">
          <Text color="fg.muted">
            Showing {(page - 1) * PAGE_SIZE + 1}-
            {Math.min(page * PAGE_SIZE, total)} of {total} topics
          </Text>
          <PaginationRoot
            count={total}
            pageSize={PAGE_SIZE}
            page={page}
            onPageChange={({ page }) => setSearch({ page })}
          >
            <Flex align="center" gap={2}>
              <PaginationPrevTrigger asChild>
                <IconButton
                  aria-label="Previous page"
                  size="sm"
                  variant="ghost"
                >
                  <FaChevronLeft />
                </IconButton>
              </PaginationPrevTrigger>
              <PaginationItems />
              <PaginationNextTrigger asChild>
                <IconButton aria-label="Next page" size="sm" variant="ghost">
                  <FaChevronRight />
                </IconButton>
              </PaginationNextTrigger>
            </Flex>
          </PaginationRoot>
        </Flex>
      </Stack>

      <DialogRoot
        open={isMergeDialogOpen}
        onOpenChange={({ open }) => setMergeDialogOpen(open)}
      >
        <DialogContent maxW="md">
          <DialogHeader>
            <DialogTitle>Merge topics</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <Text mb={3}>
              Choose which topic should remain after the merge. Questions from
              the other topics will move into the selected destination and those
              topics will be deleted.
            </Text>
            <RadioGroup
              value={destinationTopicId ?? undefined}
              onValueChange={(details) => setDestinationTopicId(details.value)}
            >
              <Stack>
                {selectedEntries.map((topic) => (
                  <Radio key={topic.id} value={topic.id}>
                    {topic.name} ({topic.question_count})
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>
          </DialogBody>
          <DialogFooter gap={3}>
            <Button variant="ghost" onClick={() => setMergeDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              colorPalette="red"
              onClick={handleConfirmMerge}
              disabled={!destinationTopicId}
              loading={mergeMutation.isPending}
            >
              Merge topics
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>

      <DialogRoot
        open={Boolean(topicToDelete)}
        onOpenChange={({ open }) => {
          if (!open) {
            setTopicToDelete(null)
          }
        }}
      >
        <DialogContent maxW="md">
          <DialogHeader>
            <DialogTitle>Delete topic</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <Text>
              Are you sure you want to delete "{topicToDelete?.name}"? This
              action cannot be undone and is only allowed for topics with zero
              questions.
            </Text>
          </DialogBody>
          <DialogFooter gap={3}>
            <Button variant="ghost" onClick={() => setTopicToDelete(null)}>
              Cancel
            </Button>
            <Button
              colorPalette="red"
              onClick={handleConfirmDelete}
              loading={deleteTopicMutation.isPending}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </PageShell>
  )
}

export default AdminTopicsPage
